/**
 * Question Refinement Modal
 * AI-powered conversational refinement for generated questions
 * Allows iterative improvements before saving to database
 */

import { useState } from 'react';
import { X, Sparkles, Send, Loader2, Check, AlertCircle, Lightbulb } from 'lucide-react';

interface RefinementMessage {
  role: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface QuestionRefinementModalProps {
  isOpen: boolean;
  question: any;
  onClose: () => void;
  onSave: (refinedQuestion: any) => void;
  onRefine: (question: any, prompt: string) => Promise<any>;
}

const QUICK_ACTIONS = [
  { label: 'Make alternatives less obvious', prompt: 'make the incorrect alternatives less obvious and more plausible to increase question difficulty' },
  { label: 'Balance alternative lengths', prompt: 'make all alternatives similar in character length (~125 chars each) to meet ANBIMA format requirements' },
  { label: 'Add numerical data', prompt: 'add realistic numerical data and financial amounts to the scenario to make it more concrete and professional' },
  { label: 'Adjust question stem length', prompt: 'adjust the question stem to be closer to 750-850 characters while maintaining clarity and all essential information' },
];

export function QuestionRefinementModal({
  isOpen,
  question,
  onClose,
  onSave,
  onRefine
}: QuestionRefinementModalProps) {
  const [messages, setMessages] = useState<RefinementMessage[]>([]);
  const [inputPrompt, setInputPrompt] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(question);
  const [showQuickActions, setShowQuickActions] = useState(true);

  if (!isOpen) return null;

  const handleRefine = async (prompt: string) => {
    if (!prompt.trim() || isRefining) return;

    // Add user message
    const userMessage: RefinementMessage = {
      role: 'user',
      content: prompt,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputPrompt('');
    setIsRefining(true);
    setShowQuickActions(false);

    try {
      // Call backend refinement API
      const refined = await onRefine(currentQuestion, prompt);

      // Add AI response message
      const aiMessage: RefinementMessage = {
        role: 'ai',
        content: `✓ Refined successfully! Applied your changes to the question.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);

      // Update current question with refined version
      setCurrentQuestion(refined);
    } catch (error: any) {
      console.error('Refinement error:', error);

      // Extract error message from response
      let errorMsg = 'Unknown error occurred';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }

      // Provide helpful suggestions based on error
      let suggestion = '';
      if (errorMsg.includes('correct answer')) {
        suggestion = ' Try a different refinement prompt or regenerate the question.';
      } else if (errorMsg.includes('parse')) {
        suggestion = ' The AI response was invalid. Please try again with a clearer instruction.';
      }

      const errorMessage: RefinementMessage = {
        role: 'ai',
        content: `✗ Refinement failed: ${errorMsg}${suggestion}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsRefining(false);
    }
  };

  const handleQuickAction = (prompt: string) => {
    handleRefine(prompt);
  };

  const handleSave = () => {
    onSave(currentQuestion);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Refine Question with AI</h2>
              <p className="text-sm text-gray-600">Iteratively improve your question before saving</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Question Preview */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
              <Lightbulb className="w-4 h-4 mr-2" />
              Current Question Preview
            </h3>
            <p className="text-sm text-blue-800 mb-3">
              {currentQuestion.stem?.substring(0, 200)}...
            </p>
            <div className="text-xs text-blue-700">
              <strong>Alternatives:</strong> {currentQuestion.alternatives?.length || 0} options
              {currentQuestion.alternatives && currentQuestion.alternatives.length > 0 && (
                <span> • {currentQuestion.alternatives[0].text?.substring(0, 50)}...</span>
              )}
            </div>
          </div>

          {/* Chat History */}
          {messages.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Refinement History</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-blue-100 border border-blue-200 ml-8'
                        : 'bg-green-50 border border-green-200 mr-8'
                    }`}
                  >
                    <div className="flex items-start space-x-2">
                      {msg.role === 'ai' && <Check className="w-4 h-4 text-green-600 mt-0.5" />}
                      <div className="flex-1">
                        <p className="text-sm text-gray-800">{msg.content}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {msg.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {showQuickActions && messages.length === 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_ACTIONS.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickAction(action.prompt)}
                    disabled={isRefining}
                    className="p-3 text-left text-sm bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              Tell AI how to refine this question:
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputPrompt}
                onChange={(e) => setInputPrompt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !isRefining && handleRefine(inputPrompt)}
                placeholder="E.g., make alternatives less obvious, add numerical data, adjust length..."
                disabled={isRefining}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 disabled:bg-gray-100"
              />
              <button
                onClick={() => handleRefine(inputPrompt)}
                disabled={isRefining || !inputPrompt.trim()}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isRefining ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Refining...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Refine</span>
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              Use natural language to describe changes. The AI will maintain ANBIMA format requirements.
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {messages.length > 0 && (
              <span className="flex items-center">
                <Check className="w-4 h-4 text-green-600 mr-1" />
                {messages.filter(m => m.role === 'ai').length} refinements applied
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-white border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center space-x-2"
            >
              <Check className="w-5 h-5" />
              <span>Save Refined Question</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
